import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DisparoInfoComponent} from './utils/disparo-info/disparo-info.component';
import {FormaEnvioComponent} from './utils/forma-envio/forma-envio.component';
import {ListaEnvioContatosComponent} from './utils/lista-envio-contatos/lista-envio-contatos.component';
import {MetricasComponent} from './utils/metricas/metricas.component';
import {DisparoAutomaticoModel} from '../../shared/model/disparo-automatico/disparo-automatico.model';
import {DisparoAutomaticoService} from '../../shared/service/disparo-automatico.service';
import {MsgService} from '../../shared/service/msg.service';
import {TemplateSalvamentoModel} from '../../shared/model/template/template-salvamento-model';
import {firstValueFrom, interval, Observable, of} from 'rxjs';
import {DisparoAutomaticoVariavelModel} from '../../shared/model/disparo-automatico/disparo-automatico-variavel.model';
import {TemplateMensagemComponent} from './utils/template-mensagem/template-mensagem.component';
import {DatePipe, NgClass} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MatDialog} from '@angular/material/dialog';
import {ModalListaContatosComponent} from './utils/modal-lista-contatos/modal-lista-contatos.component';
import {UsuarioService} from '../../shared/service/usuario.service';
import {AuthService} from '../../shared/service/auth.service';
import {ProgramacaoDisparoService} from '../../shared/service/programacao-disparo.service';
import {ProgramacaoDisparoModel} from '../../shared/model/disparo-automatico/programacao-disparo.model';
import {TipoProgramacaoDisparo} from './utils/programacao-disparo-form/programacao-disparo-form.component';
import {classeStatusDisparo} from '../../shared/model/disparo-automatico/status-disparo.util';
import {HttpErrorResponse} from '@angular/common/http';
import {SocketService} from '../../shared/service/socket.service';
import {RestricaoService} from '../../shared/service/restricao.service';
import {ButtonPadraoComponent} from '../../shared/components/button-padrao/button-padrao.component';
import {ModalConfirmacaoComponent} from '../../shared/modais/modal-confirmacao/modal-confirmacao.component';

@Component({
  selector: 'app-cadastro-disparo-automatizado',
  standalone: true,
  imports: [
    DisparoInfoComponent,
    FormaEnvioComponent,
    ListaEnvioContatosComponent,
    MetricasComponent,
    TemplateMensagemComponent,
    MatIconModule,
    NgClass,
    DatePipe,
    ButtonPadraoComponent
  ],
  templateUrl: './cadastro-disparo-automatizado.component.html',
  styleUrl: './cadastro-disparo-automatizado.component.css'
})
export class CadastroDisparoAutomatizadoComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @Input() ehMobile?: boolean;
  @Input() disparoAutomatico: DisparoAutomaticoModel = new DisparoAutomaticoModel();
  @Input() exibirProgramacao = false;
  @Input() tipoProgramacaoInicial: TipoProgramacaoDisparo = 'Padrão';
  @Input() programacaoInicial?: ProgramacaoDisparoModel;
  @Input() carregandoInicialCadastro = false;
  @Output() cancelar = new EventEmitter<boolean>();

  anexo: any;
  contatosPreview: any[] = [];
  variaveis: DisparoAutomaticoVariavelModel[] = [];
  colunasComplementares: string[] = [];
  emailVariaveisPendentes = false;
  setorId?: number;
  tipoProgramacao: TipoProgramacaoDisparo = 'Padrão';
  programacao: ProgramacaoDisparoModel = new ProgramacaoDisparoModel();
  carregandoContatos = false;
  salvando = false;
  podeReenviarIntegracao = false;
  reenviandoIntegracao = false;
  readonly classeStatusDisparo = classeStatusDisparo;

  get exibirMetricas(): boolean {
    const status = this.disparoAutomatico.status;
    return status === 'Processando' || status === 'Limite atingido' || status === 'Enviado' || status === 'Pausado';
  }

  get emProcessamento(): boolean {
    return this.disparoAutomatico.status === 'Processando';
  }

  get totalContatosProcessamento(): number {
    return Math.max(
      0,
      this.disparoAutomatico.quantidadeContatosValidos
        ?? this.disparoAutomatico.quantidadeContatos
        ?? 0
    );
  }

  get progressoProcessamento(): number {
    const progresso = Number(this.disparoAutomatico.infos?.porcProcessados ?? 0);
    return Number.isFinite(progresso)
      ? Math.min(100, Math.max(0, Math.round(progresso)))
      : 0;
  }

  get quantidadeContatosProcessados(): number {
    const total = this.totalContatosProcessamento;
    const processados = Math.round(total * this.progressoProcessamento / 100);
    return Math.min(total, Math.max(0, processados));
  }

  get motivoIntegracaoInvalida(): string {
    return this.obterMotivoIntegracaoInvalida() ?? '';
  }

  private readonly disparoAutomaricoService = inject(DisparoAutomaticoService);
  private readonly msgService = inject(MsgService);
  private readonly dialog = inject(MatDialog);
  private readonly usuarioService = inject(UsuarioService);
  private readonly authService = inject(AuthService);
  private readonly programacaoDisparoService = inject(ProgramacaoDisparoService);
  private readonly socketService = inject(SocketService);
  private readonly restricaoService = inject(RestricaoService);

  ngOnInit() {
    if (this.carregandoInicialCadastro) return;
    this.buscarSetorUsuario();
    this.carregarPermissaoReenvio();
    if (this.programacaoInicial?.programacaoId || this.programacaoInicial?.id || this.programacaoInicial?.recorrente) {
      this.programacao = Object.assign(new ProgramacaoDisparoModel(), this.programacaoInicial, {
        id: this.programacaoInicial.programacaoId ?? this.programacaoInicial.id,
        disparo: undefined,
        diasSemana: [...(this.programacaoInicial.diasSemana ?? [])],
        horarios: [...(this.programacaoInicial.horarios ?? [])]
      });
      this.tipoProgramacao = 'Recorrente';
      this.disparoAutomatico.quantidadeEnvios = this.programacao.enviosRealizados ?? 0;
    } else {
      this.tipoProgramacao = this.exibirProgramacao && !this.disparoAutomatico?.id
        ? this.tipoProgramacaoInicial
        : 'Padrão';
    }
    if (this.disparoAutomatico && (this.disparoAutomatico.id
        || this.disparoAutomatico.template
        || this.disparoAutomatico.listaContatos?.length
        || this.disparoAutomatico.origemContatos)) {
      const contatos = this.disparoAutomatico.listaContatos ?? [];
      this.anexo = {
        nomeArquivo: this.disparoAutomatico.nomeArquivo,
        extensaoArquivo: this.disparoAutomatico.extensaoArquivo,
        existente: !!this.disparoAutomatico.id && !!this.disparoAutomatico.nomeArquivo,
        listaContatos: contatos.length > 0
          ? this.verificarContatosRepetidos(contatos)
          : [],
        colunasComplementares: (this.disparoAutomatico.colunasComplementares ?? []).map(c => c.nome)
      };
      this.colunasComplementares = this.anexo.colunasComplementares;
      this.contatosPreview = this.anexo.listaContatos;
      this.variaveis = this.disparoAutomatico.variaveis ?? [];
      this.buscarVariaveisNosContatos();
      if (this.disparoAutomatico.id && this.disparoAutomatico.ehProgramacaoRecorrente) {
        this.carregarDetalhesProgramacao();
      } else if (this.disparoAutomatico.id) {
        this.carregarDetalhesDisparo();
      }
    }
    this.escutarAtualizacaoDisparo();
  }

  private carregarPermissaoReenvio(): void {
    this.restricaoService.buscarRestricoesPorRotina(19)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(restricoes => {
        this.podeReenviarIntegracao = restricoes.some(
          restricao => restricao.chave === 'REENVIAR_INTEGRACAO_DISPARO' && restricao.permite
        );
      });
  }

  reenviarIntegracao(): void {
    if (!this.disparoAutomatico.id || this.reenviandoIntegracao) return;
    this.dialog.open(ModalConfirmacaoComponent, {
      data: {
        texto: 'Reenviar a integração para todos os contatos com falha?',
        subTexto: 'Será realizada uma única tentativa manual por contato com erro.'
      }
    }).afterClosed().subscribe(confirmado => {
      if (!confirmado || !this.disparoAutomatico.id) return;
      this.reenviandoIntegracao = true;
      this.disparoAutomaricoService.reenviarFalhasIntegracao(this.disparoAutomatico.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: quantidade => {
            this.disparoAutomatico.possuiFalhaIntegracao = false;
            this.reenviandoIntegracao = false;
            this.msgService.msgSucesso(`Reenvio manual solicitado para ${quantidade} contato(s).`);
          },
          error: erro => {
            this.reenviandoIntegracao = false;
            this.msgService.msgErro(erro?.error?.message ?? 'Não foi possível reenviar a integração.');
          }
        });
    });
  }

  private readonly STATUS_SEM_SOCKET_CONFIAVEL = ['Processando', 'Limite atingido', 'Em fila', 'Buscando base'];

  private escutarAtualizacaoDisparo(): void {
    if (!this.disparoAutomatico.id && !this.programacao.id) return;
    this.socketService.connect();

    interval(6000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.disparoAutomatico.id
            && this.STATUS_SEM_SOCKET_CONFIAVEL.includes(this.disparoAutomatico.status ?? '')) {
          this.recarregarEstadoEdicao();
        }
      });

    this.socketService.escutarAtualizacaoDisparo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(atualizacao => {
        if (atualizacao.disparoId === this.disparoAutomatico.id
            && atualizacao.status !== this.disparoAutomatico.status) {
          this.recarregarEstadoEdicao();
        }
      });

    this.socketService.escutarAtualizacaoInfosDisparo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(atualizacao => {
        if (atualizacao.disparoId !== this.disparoAutomatico.id) return;
        this.disparoAutomatico.infos = { ...(this.disparoAutomatico.infos ?? {}), ...atualizacao.infos };
        if (atualizacao.custoDisparo !== undefined) {
          this.disparoAutomatico.custoDisparo = atualizacao.custoDisparo;
        }
        if (atualizacao.moedaCusto !== undefined) {
          this.disparoAutomatico.moedaCusto = atualizacao.moedaCusto;
        }
      });

    this.socketService.escutarAtualizacaoContatoDisparo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(atualizacao => {
        if (atualizacao.disparoId !== this.disparoAutomatico.id) return;
        if (atualizacao.statusEnvioIntegracao === 'FALHOU') {
          this.disparoAutomatico.possuiFalhaIntegracao = true;
        }
      });
  }

  private recarregarEstadoEdicao(): void {
    const programacaoId = this.programacao.id ?? this.disparoAutomatico.programacaoId;
    if (this.disparoAutomatico.ehProgramacaoRecorrente && programacaoId) {
      this.programacaoDisparoService.buscarProgramacao(programacaoId).subscribe(detalhe => {
        const bloqueouAgora = (this.programacao.enviosRealizados ?? 0) < 1
          && (detalhe.enviosRealizados ?? 0) >= 1;
        this.programacao = Object.assign(new ProgramacaoDisparoModel(), detalhe, {
          id: programacaoId,
          disparo: undefined,
          diasSemana: [...(detalhe.diasSemana ?? [])]
        });
        this.disparoAutomatico.quantidadeEnvios = detalhe.enviosRealizados ?? 0;
        if (bloqueouAgora) {
          this.msgService.msgAdvertencia('A primeira geração foi iniciada. Os campos da programação foram bloqueados.');
        }
      });
      return;
    }

    if (this.disparoAutomatico.id) {
      this.disparoAutomaricoService.buscarDisparo(this.disparoAutomatico.id).subscribe(atualizado => {
        const podiaEditar = !this.disparoAutomatico.status
          || this.disparoAutomatico.status === 'Agendado'
          || this.disparoAutomatico.status === 'Aguardando Envio';
        this.aplicarDetalhesDisparo(atualizado);
        const podeEditar = atualizado.status === 'Agendado' || atualizado.status === 'Aguardando Envio';
        if (podiaEditar && !podeEditar) {
          this.msgService.msgAdvertencia('O disparo entrou em processamento. Os campos foram bloqueados.');
        }
      });
    }
  }

  private carregarDetalhesDisparo(): void {
    if (!this.disparoAutomatico.id) return;
    this.disparoAutomaricoService.buscarDisparo(this.disparoAutomatico.id).subscribe({
      next: atualizado => this.aplicarDetalhesDisparo(atualizado),
      error: () => this.msgService.msgErro('Não foi possível carregar os dados completos do disparo.')
    });
  }

  private aplicarDetalhesDisparo(atualizado: DisparoAutomaticoModel): void {
    this.disparoAutomatico = Object.assign(
      new DisparoAutomaticoModel(),
      this.disparoAutomatico,
      atualizado
    );
    this.variaveis = this.disparoAutomatico.variaveis ?? [];
    this.colunasComplementares = (this.disparoAutomatico.colunasComplementares ?? [])
      .map(coluna => coluna.nome!);
    this.anexo = {
      nomeArquivo: this.disparoAutomatico.nomeArquivo,
      extensaoArquivo: this.disparoAutomatico.extensaoArquivo,
      existente: !!this.disparoAutomatico.nomeArquivo,
      listaContatos: this.anexo.listaContatos ?? [],
      colunasComplementares: this.colunasComplementares
    };
    this.buscarVariaveisNosContatos();
  }

  private carregarDetalhesProgramacao(): void {
    const programacaoId = this.programacao.id ?? this.disparoAutomatico.programacaoId;
    if (!programacaoId) return;

    this.carregandoContatos = true;
    this.programacaoDisparoService.buscarProgramacao(programacaoId).subscribe({
      next: detalhe => {
        const config = detalhe.disparo;
        if (!config) return;

        const contatos = this.verificarContatosRepetidos(config.listaContatos ?? []);
        this.disparoAutomatico = Object.assign(
          new DisparoAutomaticoModel(),
          this.disparoAutomatico,
          {
            listaContatos: contatos,
            variaveis: config.variaveis ?? [],
            colunasComplementares: config.colunasComplementares ?? [],
            integracao: config.integracao,
            quantidadeEnvios: detalhe.enviosRealizados ?? 0
          }
        );
        this.variaveis = config.variaveis ?? [];
        this.colunasComplementares = (config.colunasComplementares ?? []).map(coluna => coluna.nome!);
        this.anexo = {
          nomeArquivo: config.nomeArquivo,
          extensaoArquivo: config.extensaoArquivo,
          existente: !!config.nomeArquivo,
          listaContatos: contatos,
          colunasComplementares: this.colunasComplementares
        };
        if (this.disparoAutomatico.origemContatos !== 'BASE_DINAMICA') {
          this.contatosPreview = contatos;
        }
        this.buscarVariaveisNosContatos();
      },
      error: () => {
        this.carregandoContatos = false;
        this.msgService.msgErro('Não foi possível carregar os contatos da programação.');
      },
      complete: () => this.carregandoContatos = false
    });
  }

  verificarContatosRepetidos(listaContatos: any[]): any[] {
    const telefoneOcorrencias = new Map<string, number>();
    listaContatos.forEach(contato => {
      const telefone = contato.telefone;
      const count = telefoneOcorrencias.get(telefone) || 0;
      contato.isContatoRepetido = count >= 1;
      telefoneOcorrencias.set(telefone, count + 1);
    });
    return listaContatos;
  }

  async salvar() {
    if (this.salvando) return;

    try {
      this.salvando = true;
      const res = await firstValueFrom(this.salvarDisparoAutomatico());
      if (res) {
        this.msgService.msgSucesso(this.tipoProgramacao === 'Recorrente'
          ? 'Programação de disparo salva com sucesso.'
          : 'Disparo Automático salvo com sucesso.');
        this.cancelarCadastro(true);
      }
    } catch (erro) {
      this.tratarErroSalvamento(erro);
    } finally {
      this.salvando = false;
    }
  }

  private tratarErroSalvamento(erro: unknown): void {
    console.error('[salvarDisparoAutomatico] Falha ao salvar disparo', erro);
    const resposta = erro instanceof HttpErrorResponse ? erro.error : undefined;
    const mensagem = resposta?.detalhe
      ?? resposta?.message
      ?? (typeof resposta === 'string' ? resposta : undefined)
      ?? (erro instanceof Error ? erro.message : undefined)
      ?? 'Não foi possível salvar o disparo automático. Revise os dados e tente novamente.';
    this.msgService.msgErro(mensagem);
  }

  salvarDisparoAutomatico(): Observable<any> {
    if (!this.validarEnvioDisparoAutomatico()) {
      return of(null);
    }

    if (this.disparoAutomatico.tipoDisparo === 'Email') {
      this.disparoAutomatico.template = undefined;
      this.disparoAutomatico.fluxo = undefined;
      this.disparoAutomatico.etapa = undefined;
    } else {
      this.disparoAutomatico.assuntoEmail = undefined;
      this.disparoAutomatico.corpoHtmlEmail = undefined;
      this.disparoAutomatico.componentesEmail = [];
    }

    if (this.disparoAutomatico.template?.id) {
      const temp = new TemplateSalvamentoModel();
      temp.id = this.disparoAutomatico.template.id;
      temp.components = this.disparoAutomatico.template.components;
      this.disparoAutomatico.template = temp;
    }
    this.disparoAutomatico.variaveis = this.variaveis;

    if (this.disparoAutomatico.origemContatos === 'BASE_DINAMICA') {
      this.disparoAutomatico.listaContatos = [];
      this.disparoAutomatico.quantidadeContatos = 0;
      this.disparoAutomatico.nomeArquivo = undefined;
      this.disparoAutomatico.extensaoArquivo = undefined;
      this.disparoAutomatico.colunasComplementares = [];
      return this.persistirDisparo();
    }

    this.disparoAutomatico.origemContatos = 'EXCEL';
    this.disparoAutomatico.setorVinculoBaseId = undefined;
    this.disparoAutomatico.extensaoArquivo = this.anexo.extensaoArquivo;
    this.disparoAutomatico.nomeArquivo = this.anexo.nomeArquivo;
    const idConfiguracaoExistente = this.tipoProgramacao === 'Recorrente'
      ? (this.programacao.id ?? this.disparoAutomatico.programacaoId)
      : this.disparoAutomatico.id;
    const manterContatosExistentes = !!idConfiguracaoExistente
      && this.anexo.existente === true;
    this.disparoAutomatico.quantidadeContatos = manterContatosExistentes
      ? this.disparoAutomatico.quantidadeContatos
      : this.anexo.listaContatos?.length;
    this.disparoAutomatico.listaContatos = manterContatosExistentes
      ? undefined
      : this.anexo.listaContatos;
    this.disparoAutomatico.colunasComplementares = (this.anexo.colunasComplementares ?? [])
      .map((nome: string) => ({ nome }));

    if (
      (this.disparoAutomatico.tipoDisparo === 'Whatsapp' || this.disparoAutomatico.tipoDisparo === 'sms')
      && this.disparoAutomatico.listaContatos?.length
      && this.disparoAutomatico.listaContatos.every((contato: any) => contato.isContatoValido === false)
    ) {
      this.msgService.msgAdvertencia('Todos os contatos da lista estão inválidos');
      return of(null);
    }
    if (
      this.disparoAutomatico.tipoDisparo === 'Email'
      && this.disparoAutomatico.listaContatos?.length
      && this.disparoAutomatico.listaContatos.every((contato: any) => contato.isEmailValido === false)
    ) {
      this.msgService.msgAdvertencia('Todos os E-mails da lista estão inválidos');
      return of(null);
    }

    return this.persistirDisparo();
  }

  async enviarDisparoAutomatico() {
    if (this.salvando) return;
    if (!this.validarEnvioDisparoAutomatico()) return;

    this.salvando = true;
    try {
      if (!this.disparoAutomatico.id) {
        try {
          const res = await firstValueFrom(this.salvarDisparoAutomatico());
          if (res) {
            this.disparoAutomatico.id = res;
            this.msgService.msgSucesso('Disparo Automático salvo com sucesso.');
          }
        } catch (erro) {
          this.tratarErroSalvamento(erro);
          return;
        }
      }
      if (this.disparoAutomatico.id) {
        await firstValueFrom(this.disparoAutomaricoService.alterarStatusDisparoAutomatico('Em fila', this.disparoAutomatico.id!))
          .then(
            () => {
              this.msgService.msgSucesso('Disparo Automático adicionado na fila de envio!');
              this.cancelarCadastro(true);
            },
            erro => this.tratarErroSalvamento(erro)
          );
      }
    } finally {
      this.salvando = false;
    }
  }

  receberVariaveis(variaveis: DisparoAutomaticoVariavelModel[]) {
    this.variaveis = variaveis;
    this.buscarVariaveisNosContatos();
  }

  atualizarColunasComplementares(colunas: string[]) {
    this.colunasComplementares = colunas;
    this.buscarVariaveisNosContatos();
  }

  atualizarAnexo(anexo: any): void {
    this.anexo = anexo;
    this.contatosPreview = anexo?.listaContatos ?? [];
    this.buscarVariaveisNosContatos();
  }

  atualizarContatosPreview(contatos: any[]): void {
    this.contatosPreview = contatos ?? [];
    this.buscarVariaveisNosContatos();
  }

  buscarVariaveisNosContatos() {
    const primeiroContato = this.contatosPreview?.[0];

    if (!primeiroContato) {
      this.variaveis.forEach(variavel => {
        if (variavel.isCampoContato) {
          variavel.valor = undefined;
        }
      });
      this.variaveis = [...this.variaveis];
      return;
    }

    this.variaveis.forEach(variavel => {
      if (variavel.isCampoContato && variavel.tipoCampoContato === 'Nome') {
        variavel.valor = primeiroContato.nomeContato || primeiroContato.nome;
      } else if (variavel.isCampoContato && variavel.tipoCampoContato === 'E-mail') {
        variavel.valor = primeiroContato.email;
      } else if (variavel.isCampoContato && variavel.tipoCampoContato === 'Telefone') {
        variavel.valor = primeiroContato.telefone;
      } else if (variavel.isCampoContato && (variavel.tipoCampoContato === 'CPF' || variavel.tipoCampoContato === 'CPF/CNPJ')) {
        variavel.valor = primeiroContato.cpf;
      } else if (variavel.nomeColuna) {
        if (Array.isArray(primeiroContato.camposComplementares)) {
          const campo = primeiroContato.camposComplementares?.find((c: any) =>
            (c.nome ?? c.nomeColuna)?.localeCompare(variavel.nomeColuna, 'pt', { sensitivity: 'base' }) === 0);
          variavel.valor = campo?.valor;
        } else if (typeof primeiroContato.camposComplementares === 'object') {
          const chave = Object.keys(primeiroContato.camposComplementares ?? {}).find(nome =>
            nome.localeCompare(variavel.nomeColuna!, 'pt', { sensitivity: 'base' }) === 0);
          variavel.valor = chave ? primeiroContato.camposComplementares[chave] : undefined;
        }
      }
    });
    this.variaveis = [...this.variaveis];
  }

  cancelarCadastro(event: any) {
    this.disparoAutomatico = new DisparoAutomaticoModel();
    this.cancelar.emit(event);
  }

  validarEnvioDisparoAutomatico(): boolean {
    if (!this.disparoAutomatico.tituloDisparo || this.disparoAutomatico.tituloDisparo === '') {
      this.msgService.msgAdvertencia('Digite um titulo para o disparo automático.');
      return false;
    }
    if (!this.disparoAutomatico.tipoDisparo) {
      this.msgService.msgAdvertencia('Selecione um tipo para o disparo automático.');
      return false;
    }
    const ehEmail = this.disparoAutomatico.tipoDisparo === 'Email';
    const ehWhatsapp = this.disparoAutomatico.tipoDisparo === 'Whatsapp';

    if (!ehEmail && (!this.disparoAutomatico.template || !this.disparoAutomatico.template.id)) {
      this.msgService.msgAdvertencia('Selecione um template para o disparo automático.');
      return false;
    }
    if (ehEmail && !this.disparoAutomatico.assuntoEmail?.trim()) {
      this.msgService.msgAdvertencia('Impossivel salvar o envio de email sem assunto.');
      return false;
    }
    if (ehEmail && !this.disparoAutomatico.corpoHtmlEmail?.trim()) {
      this.msgService.msgAdvertencia('Impossivel salvar email sem corpo, favor completar a criação do mesmo ou anexar um completo.');
      return false;
    }
    if (ehWhatsapp && (!this.disparoAutomatico.fluxo || !this.disparoAutomatico.fluxo.id)) {
      this.msgService.msgAdvertencia('Selecione um fluxo para o disparo automático.');
      return false;
    }
    if (this.disparoAutomatico.origemContatos === 'BASE_DINAMICA') {
      if (!this.disparoAutomatico.setorVinculoBaseId) {
        this.msgService.msgAdvertencia('Selecione uma plataforma e uma base dinâmica para o disparo automático.');
        return false;
      }
    } else if (!this.anexo) {
      this.msgService.msgAdvertencia('Você precisa fazer o upload de uma lista de contatos para salvar/enviar esse disparo.');
      return false;
    }
    if (!this.validarIntegracao()) {
      return false;
    }
    if (!ehEmail && !this.validarVariaveisTemplate()) {
      this.msgService.msgAdvertencia('Preencha o campo de variável.');
      return false;
    }
    if (ehEmail && this.emailVariaveisPendentes) {
      this.msgService.msgAdvertencia(
        'A lista de contatos removeu variáveis de colunas complementares do e-mail. ' +
        'Edite o e-mail (Montador de E-mail) para completar essas variáveis antes de salvar.');
      return false;
    }
    if (this.tipoProgramacao === 'Recorrente' && !this.validarProgramacao()) {
      return false;
    }
    return true;
  }

  private validarIntegracao(): boolean {
    const motivo = this.obterMotivoIntegracaoInvalida();
    if (!motivo) return true;
    this.msgService.msgAdvertencia(motivo);
    return false;
  }

  private obterMotivoIntegracaoInvalida(): string | null {
    const integracao = this.disparoAutomatico.integracao;
    if (!integracao) return null;
    if (!integracao.id) return 'Selecione uma integração ou desative a opção Realizar integração.';
    if (!integracao.personalizada) return null;
    if (!integracao.payload?.trim()) return 'Informe o payload da integração personalizada.';

    try {
      const json = JSON.parse(integracao.payload);
      if (!json || Array.isArray(json) || typeof json !== 'object' || Object.keys(json).length === 0) {
        return 'O payload da integração deve ser um objeto JSON com ao menos um campo.';
      }
    } catch {
      return 'O payload da integração personalizada não é um JSON válido.';
    }

    const variaveis = integracao.variaveis ?? [];
    if (variaveis.some(item => !item.ordem || !item.campo)) {
      return 'Selecione o campo de todas as variáveis da integração.';
    }
    const ordens = variaveis.map(item => item.ordem!);
    if (new Set(ordens).size !== ordens.length) {
      return 'As variáveis da integração não podem repetir a mesma ordem.';
    }
    const marcadores = new Set(
      [...integracao.payload.matchAll(/\{\{(\d+)}}/g)].map(item => Number(item[1])));
    const ordensSet = new Set(ordens);
    if (marcadores.size !== ordensSet.size || [...marcadores].some(ordem => !ordensSet.has(ordem))) {
      return 'Cada marcador do payload deve possuir uma variável correspondente.';
    }
    return null;
  }

  private persistirDisparo(): Observable<number> {
    if (this.tipoProgramacao === 'Padrão') {
      return this.disparoAutomaricoService.salvarDisparoAutomarico(this.disparoAutomatico);
    }

    const payload = this.normalizarProgramacao();
    payload.disparo = Object.assign(new DisparoAutomaticoModel(), this.disparoAutomatico, {
      programacao: undefined
    });
    payload.disparo.dataAgendamento = undefined;
    payload.disparo.horaAgendamento = undefined;
    payload.disparo.dataEnvio = undefined;
    payload.disparo.status = undefined;
    return this.programacaoDisparoService.salvarProgramacao(payload);
  }

  private validarProgramacao(): boolean {
    const programacao = this.programacao;
    if (!programacao.frequencia) {
      this.msgService.msgAdvertencia('Selecione a frequência da recorrência.');
      return false;
    }
    if (!programacao.dataInicio || !programacao.hora) {
      this.msgService.msgAdvertencia('Informe a data e o horário da recorrência.');
      return false;
    }
    if (programacao.frequencia === 'Diário') {
      const horarios = (programacao.horarios ?? []).map(horario => horario?.replace(/\s/g, '') ?? '');
      if (!horarios.length || horarios.some(horario => !/^([01]\d|2[0-3]):[0-5]\d$/.test(horario))) {
        this.msgService.msgAdvertencia('Informe todos os horários válidos das gerações diárias.');
        return false;
      }
      if (new Set(horarios).size !== horarios.length) {
        this.msgService.msgAdvertencia('Os horários das gerações diárias não podem se repetir.');
        return false;
      }
    }
    const hoje = new Date();
    const hojeLocal = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
    if (!programacao.id && programacao.dataInicio < hojeLocal) {
      this.msgService.msgAdvertencia('A data de início não pode ser anterior à data de hoje.');
      return false;
    }
    if ((programacao.intervalo ?? 0) < 1) {
      this.msgService.msgAdvertencia('O intervalo da recorrência deve ser maior ou igual a 1.');
      return false;
    }
    if (programacao.frequencia === 'Semanal' && !programacao.diasSemana?.length) {
      this.msgService.msgAdvertencia('Selecione ao menos um dia da semana.');
      return false;
    }
    if (programacao.frequencia === 'Mensal') {
      if (programacao.tipoRecorrenciaMensal === 'Dia do Mês'
        && !programacao.ultimoDiaMes
        && ((programacao.diaMes ?? 0) < 1 || (programacao.diaMes ?? 0) > 31)) {
        this.msgService.msgAdvertencia('Informe um dia do mês entre 1 e 31.');
        return false;
      }
      if (programacao.tipoRecorrenciaMensal === 'Dia da Semana'
        && (programacao.posicaoSemanaMes == null || programacao.diaSemanaMes == null)) {
        this.msgService.msgAdvertencia('Informe a posição e o dia da semana da recorrência mensal.');
        return false;
      }
    }
    if (programacao.frequencia === 'Personalizada' && !programacao.unidadePersonalizada) {
      this.msgService.msgAdvertencia('Selecione a unidade da recorrência personalizada.');
      return false;
    }
    if (programacao.tipoTermino === 'Data') {
      if (!programacao.dataTermino) {
        this.msgService.msgAdvertencia('Informe a data de término da recorrência.');
        return false;
      }
      if (programacao.dataTermino < programacao.dataInicio) {
        this.msgService.msgAdvertencia('A data de término não pode ser anterior à data inicial.');
        return false;
      }
    }
    if (programacao.tipoTermino === 'N. de Envios' && (programacao.numeroEnviosTermino ?? 0) < 1) {
      this.msgService.msgAdvertencia('Informe uma quantidade de envios maior ou igual a 1.');
      return false;
    }
    return true;
  }

  private normalizarProgramacao(): ProgramacaoDisparoModel {
    const payload = Object.assign(new ProgramacaoDisparoModel(), this.programacao, {
      disparo: undefined,
      diasSemana: [...(this.programacao.diasSemana ?? [])],
      horarios: (this.programacao.horarios ?? [])
        .filter((horario): horario is string => typeof horario === 'string')
    });

    if (payload.frequencia !== 'Semanal') {
      payload.diasSemana = [];
    }
    if (payload.frequencia !== 'Diário') {
      payload.horarios = payload.hora ? [payload.hora] : [];
    } else {
      payload.horarios = payload.horarios.map(horario => horario.replace(/\s/g, '')).filter(Boolean);
      payload.hora = payload.horarios[0];
    }
    if (payload.frequencia !== 'Mensal') {
      payload.tipoRecorrenciaMensal = undefined;
      payload.diaMes = undefined;
      payload.ultimoDiaMes = undefined;
      payload.posicaoSemanaMes = undefined;
      payload.diaSemanaMes = undefined;
    } else if (payload.tipoRecorrenciaMensal === 'Dia do Mês') {
      payload.posicaoSemanaMes = undefined;
      payload.diaSemanaMes = undefined;
      if (payload.ultimoDiaMes) {
        payload.diaMes = undefined;
      }
    } else {
      payload.diaMes = undefined;
      payload.ultimoDiaMes = undefined;
    }
    if (payload.frequencia !== 'Personalizada') {
      payload.unidadePersonalizada = undefined;
    }
    if (payload.tipoTermino !== 'Data') {
      payload.dataTermino = undefined;
    }
    if (payload.tipoTermino !== 'N. de Envios') {
      payload.numeroEnviosTermino = undefined;
    }
    return payload;
  }

  abrirModalListaContatos() {
    this.dialog.open(ModalListaContatosComponent, {
      width: '80vw', height: '100vh', maxWidth: '400px',
      position: {
        top: '0',
        right: '0'
      },
      data: { anexo: this.anexo },
      panelClass: 'slide-modal'
    });
  }

  validarVariaveisTemplate(): boolean {
    const variaveisTemplate = this.disparoAutomatico.template?.components
      .filter(componente => componente.type === 'BODY' && componente.example)
      .flatMap(componente => componente.example!)
      .map(exemplo => exemplo.ordem) || [];
    return variaveisTemplate.every(ordem => this.variaveis.some(v => v.ordem === ordem && !!v.valor));
  }

  buscarSetorUsuario() {
    if (this.disparoAutomatico?.setor?.id) {
      this.setorId = this.disparoAutomatico.setor.id;
      return;
    }
    this.usuarioService.buscarUsuarioComplete(this.authService.getUsuarioLogado().id!).subscribe({
      next: data => this.setorId = data.setorDTO?.id
    });
  }

  mostraBtnReenvioInteg(){
    if(this.podeReenviarIntegracao && this.disparoAutomatico.possuiIntegracao && this.disparoAutomatico.possuiFalhaIntegracao){
      return true
    }
    return false
  }

}
